import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  FormControl,
  Grid,
  IconButton,
  Input,
  InputLabel,
  LinearProgress,
  Menu,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TableSortLabel,
  TextField,
  Tooltip,
  Typography,
} from "@mui/material";
import { t } from "i18next";
import { Panel, PanelGroup, PanelResizeHandle } from "react-resizable-panels";
import { useRecoilState, useRecoilValue, useResetRecoilState, useSetRecoilState } from "recoil";
import { PlayArrowIcon } from "../../icons";
import {
  continueTrainState,
  openDefectPreviewState,
  parentModelState,
  projectInfoState,
  selectedImageState,
  thresholdCustomState,
} from "../../recoil";
import { useEffect, useMemo, useRef, useState } from "react";
import { extractFileName, getDatasetInfo, getFile, StopIcon, truncateString } from "common-lib";

import { extractImgPaths, transformDatasetFiles } from "../../utils/datasetFileUtil";
import {
  continueTrainInfoState,
  epochResultsState,
  heatmapModelState,
  imageViewModeState,
  myAIModuleState,
  openHistogramPopState,
  trainingStateAtom,
  trainUuidState,
  validatingStateAtom,
  validListStateAtom,
} from "../../recoil/upgrade";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { getMyModuleServer, importAIModules, startTrain, clearImgData, updateAIModules } from "common-lib";
import { ensureAIServer } from "../../services/aiServer";
import { IImageInfo } from "@types";
import { useTrainHandler } from "../../hooks/useTrainHandler";
import { excludeFromTrain, getLatestTrainHistory, getProject } from "../../services/projectService";
import { getLatestInspResult } from "../../services/inspLogService";
import { FixedSizeList as List } from "react-window";
import DefectPreview, { SaveOptionType } from "./DefectPreview";
import { InspectionDataFlowDiagram, VirtualizedSelectableList } from "common_ui_lib";
import { getInspModel } from "../../services/modelService";
import { startEpochTrain, stopEpochTrain } from "../../services/trainService";
import TrainingProgressDropdown, { resultItemType } from "./TrainingProgressDropdown";
import SnapshotModal from "../train/SnapshotModal";
import { useEpochStream } from "../../hooks/useEpochStream";
import { AITableCell } from "./AITableCell";
import { useTutorial } from "../../context/TutorialContext";
import { TUTORIALS } from "../../tutorial";
import { TUTORIAL_EPOCH, TUTORIAL_OVERKILL_CONDITION, TUTORIAL_TRAIN_COUNT } from "../../tutorial/constants";
import { swBridge } from "../../utils/sw-register";
import { useConfirm } from "../confirm/ConfirmProvider";
import { CompareBasketPopover, useCompareBasket } from "./CompareBasket";
import CollectionsIcon from "@mui/icons-material/Collections";
import { Crosshair, Info, ListFilter } from "lucide-react";
import TableToolsPanel, {
  exportCsv,
  getMismatchPairs,
  matchesMismatch,
  MismatchFilter,
  nextNearThreshold,
  ThresholdJump,
} from "./ProjectTableToolbar/TableToolsPanel";

interface InspProjectTableProps {
  dataSetId: number;
}

const COLUMN_WEIGHT: Record<string, number> = { no: 1, imageName: 3, label: 2, AILabel: 2, score: 2 };
const getColumnWeight = (field: string) => COLUMN_WEIGHT[field] ?? 2;

export default function InspProjectTable({ dataSetId }: InspProjectTableProps) {
  const { id } = useParams();
  const confirm = useConfirm();

  const [selectedRow, setSelectedRow] = useRecoilState(selectedImageState);
  const [trainList, setTrainList] = useState<IImageInfo[]>([]);
  const [validList, setValidList] = useRecoilState(validListStateAtom);

  const [trainingState, setTrainingState] = useRecoilState(trainingStateAtom);
  const [validatingState, setValidatingState] = useRecoilState(validatingStateAtom);
  const [trainedImageCount, setTrainedImageCount] = useState(0);
  const [autoValid, setAutoValid] = useState<boolean>(false);
  const [openHelpImageList, setOpenHelpImageList] = useState(false);

  const projectData = useRecoilValue(projectInfoState);
  const [myAIModule, setMyAIModule] = useRecoilState(myAIModuleState);
  const trainUuid = useRecoilValue(trainUuidState);
  const [epoch, setEpoch] = useState<number>(10);
  const [overkillCondition, setOverkillCondition] = useState<number>(50);
  const [selectedTrainCount, setSelectedTrainCount] = useState<number>(0);
  // 페이지 진입 시 이미 진행 중이던 학습(자동학습 등)의 에폭당 이미지 수
  const [runningImgPerEpoch, setRunningImgPerEpoch] = useState<number | null>(null);
  const resetImageViewMode = useResetRecoilState(imageViewModeState);
  const setImageViewMode = useSetRecoilState(imageViewModeState);
  const setHeatmapModel = useSetRecoilState(heatmapModelState);
  const [inspResult, setInspResult] = useState<any>([]);
  const [progress, setProgress] = useState<{
    count: number;
    totalCount: number;
  }>({ count: 0, totalCount: 0 });
  const listRef = useRef<List>(null);
  const trainFields = ["no", "imageName", "label"];
  const validFields = ["no", "imageName", "label", "AILabel", "score"];
  // 헤더(table-layout:fixed)와 바디(flex 행)는 레이아웃 엔진이 달라서, 같은 가중치로 폭을 맞춰야 열이 안 틀어진다
  const headerWidth = (field: string, fields: string[]) =>
    `${(getColumnWeight(field) / fields.reduce((sum, f) => sum + getColumnWeight(f), 0)) * 100}%`;
  const [parentModel, setParentModel] = useRecoilState(parentModelState);
  const [epochResults, setEpochResults] = useRecoilState(epochResultsState);
  // 우클릭한 epoch 카드의 저장 모델 → 스냅샷 모달
  const [snapshotTarget, setSnapshotTarget] = useState<resultItemType | null>(null);
  // 스냅샷 모듈 spec이 프로젝트당 1개라 히스토그램 모달과 동시에 띄우지 않는다
  const [openHistogramPop, setOpenHistogramPop] = useRecoilState(openHistogramPopState);
  useEffect(() => {
    if (openHistogramPop) setSnapshotTarget(null);
  }, [openHistogramPop]);
  const hasValidModelRef = useRef<Record<number, boolean>>({});
  const navigate = useNavigate();
  // 페이지 재진입 시 직전 학습이 어떻게 끝났는지 보여주기 위한 최근 학습 결과
  const [lastTrain, setLastTrain] = useState<{
    trainHistory: { status: string; finish_at: string | null; create_at: string } | null;
    savedModels: { model_uuid: string; name: string }[];
  } | null>(null);
  const { run, nextSubStep, tutorialKey, stepIndexRef } = useTutorial();

  const { inspStatus } = useTrainHandler({
    id,
    step: "분류",
    label: "",
    projectDataStep: "분류",
    t,
    isTrainable: true,
  });

  const fetchDsfile = async () => {
    const dsInfo = await getDatasetInfo(dataSetId);
    const dsFileInfo = await getFile(dsInfo.data.path);
    const fileList = transformDatasetFiles(dsFileInfo.data);
    // 데이터셋 파일의 id는 파일 내 순번이라 DB id가 아님 → path로 projectImageLabel.id를 매칭해 덮어씀.
    // 매칭 실패 시 undefined로 둬서 ImagePart의 컨투어 저장(/segment/save)이 엉뚱한 행을 덮어쓰지 않게 함
    const { project } = await getProject(Number(id));
    const labelIdByPath = new Map<string, number>(
      (project.images as IImageInfo[]).map((img) => [img.path, img.id!] as [string, number]),
    );
    const withLabelId = (rows: IImageInfo[]) => rows.map((r) => ({ ...r, id: labelIdByPath.get(r.path) }));
    // transformDatasetFiles는 JS라 반환 타입이 never[]로 추론됨 → fileList에 재할당하지 않고 지역 변수로 받음
    const train = withLabelId(fileList.train);
    const valid = withLabelId(fileList.valid);
    console.log("fileList", { train, valid });
    setTrainList(train);
    setSelectedTrainCount(train.length);
    // 프로젝트 진입 시 학습 리스트 첫 이미지를 바로 ImagePart에 띄움 (이미 선택된 행이 있으면 유지)
    if (train.length > 0) setSelectedRow((prev) => (prev?.path ? prev : train[0]));
    setValidList((prev) => ({
      ...prev,
      [Number(id)]: valid,
    }));
    const mergedValidList = await fetchInspResult(valid);
    if (mergedValidList) {
      setValidList((prev) => ({
        ...prev,
        [Number(id)]: mergedValidList,
      }));
    }
  };

  const fetchStatus = async () => {
    const response = await getLatestTrainHistory(Number(id));
    const trainHistoryData = response.trainHistory;
    setLastTrain({ trainHistory: trainHistoryData, savedModels: response.savedModels ?? [] });
    console.log("trainHistoryData", trainHistoryData);

    if (trainHistoryData) {
      if (trainHistoryData.status === "Training" || trainHistoryData.status === "Pending") {
        setTrainingState((prev) => ({ ...prev, [Number(id)]: true }));
        setEpoch(trainHistoryData.epoch);
        try {
          setRunningImgPerEpoch(JSON.parse(trainHistoryData.multi_detail || "{}")?.info?.img_per_epoch ?? null);
        } catch {}
      } else {
        setTrainingState((prev) => ({ ...prev, [Number(id)]: false }));
      }
    }
  };

  useEffect(() => {
    fetchStatus().catch(console.error);
    fetchDsfile().catch((e) => toast.error(e.message));
  }, []);

  useEffect(() => {
    if (!run || tutorialKey !== "inspection-project" || trainList.length === 0) return;
    setSelectedTrainCount(Math.min(TUTORIAL_TRAIN_COUNT, trainList.length));
    setEpoch(TUTORIAL_EPOCH);
    setOverkillCondition(TUTORIAL_OVERKILL_CONDITION);
  }, [run, tutorialKey, trainList.length]);

  useEpochStream({
    enabled: trainingState[Number(id)] || validatingState[Number(id)],
    trainUuid,
    projectData,
    myAIModule,
    id,
    hasValidModelRef,
    setMyAIModule,
    onProgress: ({ count, totalCount, epochResults }) => {
      setProgress({ count, totalCount });
      setEpochResults(epochResults);
    },
    onValidProgress: ({ extracted, count, totalCount }) => {
      setInspResult(extracted);
      setProgress({ count, totalCount });
    },
    onComplete: async () => {
      setValidatingState((prev) => ({ ...prev, [Number(id)]: false }));
      fetchStatus().catch(console.error);

      if (run) {
        const currentSubStep = TUTORIALS[tutorialKey][stepIndexRef.current];
        if (currentSubStep?.nextTrigger === "#insp-result-list") {
          nextSubStep();
        }
      }

      if (hasValidModelRef.current[Number(id)] === false) {
        toast.warn(t("NoValidModelMsg"));
        return;
      }

      toast.success(t("InspectionCompleteMsg"));
      const sortingData = await fetchInspResult(validList[Number(id)] || []);
      if (sortingData) {
        setValidList((prev) => ({ ...prev, [Number(id)]: sortingData }));
      }
    },
    onError: (err) => {
      setValidatingState((prev) => ({ ...prev, [Number(id)]: false }));
      console.error("스트림 오류:", err);
    },
  });

  const handleRowClick = (row: any) => {
    setSelectedRow(row);
  };

  // 학습 리스트 우클릭 → 학습 제외: 데이터셋 파일에서 train → valid 로 옮기고 목록을 다시 읽는다
  const handleExcludeFromTrain = async (row: IImageInfo) => {
    setRowMenu(null);
    try {
      await excludeFromTrain(Number(id), [row.path]);
      await fetchDsfile();
      toast.success(t("ExcludedFromTrainMsg", "학습에서 제외하고 검증 리스트로 옮겼습니다."));
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  // 검증 리스트 도구: threshold 이동 / 불일치 필터 / CSV (ImageTable과 동일 기능)
  const validListRef = useRef<List>(null);
  const isValidating = !!validatingState[Number(id)];
  const validItems = validList[Number(id)] || [];
  const thresholdCustom = useRecoilValue(thresholdCustomState);
  const continueTrainInfo = useRecoilValue(continueTrainInfoState);
  const threshold: string | undefined = (
    continueTrainInfo.id ? thresholdCustom : myAIModule?.options?.labelscore?.para?.threshold_list?.[0]
  )?.toFixed(4);
  const [toolsAnchor, setToolsAnchor] = useState<HTMLElement | null>(null);
  // 비교 이미지 담기: 검증 리스트 행 우클릭으로 담고, 디펙 미리보기 풀스크린에서 원본 옆에 띄워 비교
  const basket = useCompareBasket(String(id));
  const [basketAnchor, setBasketAnchor] = useState<HTMLElement | null>(null);
  const [rowMenu, setRowMenu] = useState<{ row: IImageInfo; x: number; y: number; from: "train" | "valid" } | null>(
    null,
  );
  const [mismatchFilter, setMismatchFilter] = useState<MismatchFilter>(null);
  const mismatchPairs = useMemo(() => getMismatchPairs(validItems), [validItems]);
  // 검증 리스트 헤더 정렬 (imageName / score), 같은 컬럼 재클릭 시 asc ↔ desc
  const [sort, setSort] = useState<{ field: string; dir: "asc" | "desc" } | null>(null);
  // ponytail: 검증 리스트는 라벨 수정이 없어 스냅샷 없이 매번 필터링
  const viewValidItems = useMemo(() => {
    const rows = mismatchFilter ? validItems.filter((r) => matchesMismatch(mismatchFilter, r)) : validItems;
    if (!sort) return rows;
    const dir = sort.dir === "asc" ? 1 : -1;
    // score 미검증(undefined)은 항상 아래로
    return [...rows].sort((a, b) => {
      if (sort.field === "score") {
        if (typeof a.score !== "number") return typeof b.score !== "number" ? 0 : 1;
        if (typeof b.score !== "number") return -1;
        return (a.score - b.score) * dir;
      }
      return extractFileName(a.path).localeCompare(extractFileName(b.path), undefined, { numeric: true }) * dir;
    });
  }, [validItems, mismatchFilter, sort]);

  // 검증 시작 시 이전 결과 기준 필터 해제
  useEffect(() => {
    if (isValidating) {
      setMismatchFilter(null);
      setToolsAnchor(null);
    }
  }, [isValidating]);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      const key = e.key.toLowerCase();
      if (key !== "arrowup" && key !== "arrowdown") return;
      const tag = (e.target as HTMLElement)?.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA") return;
      if (!selectedRow?.path) return;

      const isTrain = trainList.some((row) => row.path === selectedRow.path);
      const list = isTrain ? trainList : viewValidItems;
      const index = list.findIndex((row) => row.path === selectedRow.path);
      if (index === -1) return;

      e.preventDefault();
      const nextIndex = key === "arrowdown" ? index + 1 : index - 1;
      const next = list[nextIndex];
      if (!next) {
        toast.warning(t(key === "arrowdown" ? "LastImageMsg" : "FirstImageMsg"));
        return;
      }
      setSelectedRow(next);
      (isTrain ? listRef : validListRef).current?.scrollToItem(nextIndex, "smart");
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [selectedRow, trainList, viewValidItems]);

  const jumpRef = useRef<ThresholdJump>(null);
  const handleJumpToThreshold = () => {
    if (threshold === undefined || !viewValidItems.length) return;
    const { index, jump } = nextNearThreshold(viewValidItems, Number(threshold), jumpRef.current, selectedRow?.path);
    jumpRef.current = jump;
    setSelectedRow(viewValidItems[index]);
    validListRef.current?.scrollToItem(index, "center");
  };

  const handleTrain = async (random: number, saveOption: SaveOptionType) => {
    if (myAIModule && trainUuid) {
      setRunningImgPerEpoch(null);
      setEpochResults([]);
      resetImageViewMode();

      // 이어학습인 경우
      if (parentModel) {
        const modelData = await getInspModel(parentModel.id);
        // 해당 모델로 import
        await importAIModules(
          id,
          true,
          false,
          false,
          myAIModule.serverAddress,
          modelData.options.AI_Vision,
          parentModel.uuid,
        );
      } else {
        // 신규학습 -> project에 연결된 inspModel_id에서 불러와서 import
        if (projectData && projectData.inspModel_id) {
          const modelData = await getInspModel(projectData.inspModel_id);
          // 원래 초기 모델로 import
          setParentModel({ id: modelData.id, uuid: modelData.uuid });
        }
      }

      await clearImgData(myAIModule.serverAddress, trainUuid);

      // myAIModule 데이터 업데이트
      const response = await getMyModuleServer(id, null);
      const updateAIModuleData = {
        specId: trainUuid,
        serverAddress: response.serverAddress,
        ...response.module,
      };
      setMyAIModule(updateAIModuleData);

      if (updateAIModuleData) {
        const datasetData = await getDatasetInfo(projectData?.dataSet_id!);
        try {
          await startEpochTrain({
            uuid: id!!,
            img_count: projectData?.counts.total_count || 0,
            dataset_path: datasetData.data.path,
            model_id: 5,
            ip: myAIModule.serverAddress,
            epoch: epoch,
            img_per_epoch: selectedTrainCount,
            auto_valid: autoValid,
            saveOption: null,
            parentModel: parentModel,
            overkillCondition: overkillCondition / 100,
          });
        } catch (error: any) {
          // 백엔드에서 trainHistory를 Failed 처리함 → polling 시작하지 않음
          setTrainingState((prev) => ({ ...prev, [Number(id)]: false }));
          swBridge.stopPolling(trainUuid);
          toast.error(error?.response?.data?.error ?? "학습 요청에 실패했습니다");
          return;
        }
        setTrainingState((prev) => ({
          ...prev,
          [Number(id)]: true,
        }));
      }

      if (run) {
        setTimeout(() => nextSubStep(), 500);
      }
    }
  };

  const handleStopTrain = async () => {
    if (myAIModule && id) {
      try {
        const ok = await confirm({
          title: t("StopConfirmTitle"),
          message: t("StopConfirmMsg"),
          destructive: true,
          confirmText: t("Confirm"),
        });
        if (!ok) return;

        await stopEpochTrain({
          uuid: id,
          ip: myAIModule?.serverAddress,
        });

        setTrainingState((prev) => ({ ...prev, [Number(id)]: false }));
        // polling은 trainUuid로 시작했으므로 같은 키로 멈춘다 (label이 있으면 id와 다름)
        if (trainUuid) {
          swBridge.stopPolling(trainUuid);
          swBridge.clearCache(trainUuid);
        }
        toast.success(t("TrainStoppedMsg"));
      } catch (error) {
        console.error(error);
      }
    }
  };

  const [openDefectPreview, setOpenDefectPreview] = useRecoilState(openDefectPreviewState);

  const fetchInspResult = async (validList: IImageInfo[]) => {
    const response = await getLatestInspResult(Number(id));

    if (!response?.results?.length) return null;

    // 결과의 path가 item과 다를 수 있으므로 id는 결과 path 기준으로 다시 매칭 (projectImageLabel.id 유지)
    const labelIdByPath = new Map(validList.map((item) => [item.path, item.id]));
    return validList.map((item: IImageInfo, index: number) => {
      const res = response.results[index];
      if (!res) return item;
      return {
        id: labelIdByPath.get(res.path ?? item.path),
        path: res.path ?? item.path,
        label: res.label ?? res.label ?? item.label,
        AILabel: res.AILabel ?? res.AILabel ?? "",
        score: typeof res.score === "number" ? res.score : item.score,
        // status: res.status ?? item.status,
      };
    });
  };

  const renderCellContent = (field: string, row: IImageInfo, index: number) => {
    switch (field) {
      case "no":
        return index;
      case "imageName":
        return (
          <Tooltip title={row.path}>
            <span>{truncateString(extractFileName(row.path), 10)}</span>
          </Tooltip>
        );
      case "label":
        return row.label;
      case "AILabel":
        return (
          <AITableCell inspStatus={inspStatus} inspResult={inspResult} row={row} isVisible={true} column="aiLabel" />
        );
      case "score":
        return (
          <AITableCell inspStatus={inspStatus} inspResult={inspResult} row={row} isVisible={true} column="score" />
        );
      case "general":
        let format: any = {};
        try {
          if (row.general && typeof row.general === "string") {
            format = JSON.parse(row.general);
          } else if (typeof row.general === "object" && row.general !== null) {
            format = row.general;
          } else {
            format = {};
          }
        } catch (e) {
          console.warn("Invalid JSON in row.general:", row.general);
          format = {};
        }

        return format.high_pixel ?? "";
      default:
        return "";
    }
  };

  return (
    <>
      {openDefectPreview && (
        <DefectPreview
          dataSetId={dataSetId}
          openDefectPreview={openDefectPreview}
          trainList={trainList}
          epoch={epoch}
          setEpoch={setEpoch}
          overkillCondition={overkillCondition}
          setOverkillCondition={setOverkillCondition}
          selectedTrainCount={selectedTrainCount}
          setSelectedTrainCount={setSelectedTrainCount}
          setOpenDefectPreview={setOpenDefectPreview}
          onTrain={handleTrain}
        />
      )}
      <Grid
        container
        sx={{
          width: "100%",
          height: "100%",
          overflowY: "auto",
        }}
        // id="table-container"
        id="insp-img-table"
        tabIndex={0}
      >
        <PanelGroup direction="vertical">
          {/* 학습 리스트 */}
          <Panel defaultSize={50} minSize={10}>
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor: "var(--basic-color)",
                  pl: 1,
                  py: 0.5,
                  height: "40px",
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", minWidth: 0, overflow: "hidden" }}>
                  <Typography fontWeight={700} noWrap sx={{ flexShrink: 0 }}>
                    {t("TrainList")}
                  </Typography>
                  <IconButton onClick={() => setOpenHelpImageList(true)}>
                    <Info size={16} />
                  </IconButton>
                  {!trainingState[Number(id)] && !validatingState[Number(id)] && (
                    <LastTrainChip lastTrain={lastTrain} onClick={() => navigate(`/main/project/${id}/history`)} />
                  )}
                </Box>
                {!trainingState[Number(id)] ? (
                  <Button
                    id="open-train-option"
                    sx={{ color: "var(--main-text-color)" }}
                    disabled={!myAIModule}
                    onClick={async () => {
                      if (await ensureAIServer(trainUuid)) setOpenDefectPreview(true);
                    }}
                  >
                    <PlayArrowIcon />
                  </Button>
                ) : (
                  <Button
                    id="open-train-option"
                    sx={{ color: "var(--main-text-color)" }}
                    disabled={!myAIModule}
                    onClick={handleStopTrain}
                  >
                    <StopIcon />
                  </Button>
                )}
              </Box>
              {trainingState[Number(id)] && (
                <Box sx={{ position: "relative" }}>
                  <TrainingProgressDropdown
                    totalEpoch={epoch}
                    results={epochResults}
                    outerState="epoch"
                    imgPerEpoch={runningImgPerEpoch ?? selectedTrainCount}
                    onSnapshot={(result) => {
                      setOpenHistogramPop(false);
                      setHeatmapModel({
                        uuid: result.model_uuid,
                        label: `${t("Epoch")} ${result.epoch}`,
                        source: "epoch",
                      });
                      setSnapshotTarget(result);
                    }}
                    onViewHeatmap={(result) => {
                      setHeatmapModel({
                        uuid: result.model_uuid,
                        label: `${t("Epoch")} ${result.epoch}`,
                        source: "epoch",
                      });
                      setImageViewMode("scoremap");
                    }}
                  />
                </Box>
              )}
              <TableContainer sx={{ flex: 1, overflowY: "auto", height: "100%" }}>
                <Table
                  size="small"
                  sx={{
                    tableLayout: "fixed",
                  }}
                  stickyHeader
                >
                  <TableHead>
                    <TableRow>
                      {trainFields.map((field) => (
                        <TableCell
                          key={field}
                          align="center"
                          sx={{ padding: "0 4px", width: headerWidth(field, trainFields) }}
                        >
                          {field === "no" ? "id" : field}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                </Table>
                <VirtualizedSelectableList
                  items={trainList}
                  itemHeight={40}
                  heightOffset={40}
                  selectedItem={selectedRow}
                  listRef={listRef}
                  onRowClick={handleRowClick}
                  onRowContextMenu={(row: IImageInfo, e: React.MouseEvent) => {
                    e.preventDefault();
                    setRowMenu({ row, x: e.clientX, y: e.clientY, from: "train" });
                  }}
                  renderRow={(row: IImageInfo, index: number) =>
                    trainFields.map((field, colIndex) => (
                      <div
                        key={colIndex}
                        style={{
                          flex: `${getColumnWeight(field)} 1 0`,
                          minWidth: 0,
                          overflow: "hidden",
                          whiteSpace: "nowrap",
                          textAlign: "center",
                          padding: "0 4px",
                        }}
                      >
                        {renderCellContent(field, row, index)}
                      </div>
                    ))
                  }
                />
                {/* </TableBody> */}
              </TableContainer>
            </Box>
          </Panel>

          <PanelResizeHandle
            style={{
              height: "6px",
              background:
                "linear-gradient(to bottom, var(--dk-border, #ccc) 0%, var(--dk-border, #ccc) 40%, var(--dk-surface, white) 40%, var(--dk-surface, white) 60%, var(--dk-border, #ccc) 60%, var(--dk-border, #ccc) 100%)",
              cursor: "row-resize",
              transition: "background 0.2s",
              marginTop: "3px",
              marginBottom: "3px",
            }}
            onMouseEnter={(e) =>
              ((e.currentTarget as any).style.background =
                "linear-gradient(to bottom, #66aaff 0%, #66aaff 40%, var(--dk-surface, white) 40%, var(--dk-surface, white) 60%, #66aaff 60%, #66aaff 100%)")
            }
            onMouseLeave={(e) =>
              ((e.currentTarget as any).style.background =
                "linear-gradient(to bottom, var(--dk-border, #ccc) 0%, var(--dk-border, #ccc) 40%, var(--dk-surface, white) 40%, var(--dk-surface, white) 60%, var(--dk-border, #ccc) 60%, var(--dk-border, #ccc) 100%)")
            }
          />
          <Panel id="insp-result-list">
            <Box
              sx={{
                height: "100%",
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  backgroundColor: "var(--basic-color)",
                  pl: 1,
                  py: 0.5,
                  height: "40px",
                }}
              >
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0.5,
                    justifyContent: "space-between",
                    width: "100%",
                  }}
                >
                  <Typography fontWeight={700} noWrap>
                    {t("ValidationList")}
                  </Typography>
                  <Box>
                    <Tooltip title={t("JumpToThresholdTooltip")}>
                      <span>
                        {isValidating ? (
                          <IconButton size="small" disabled>
                            <Crosshair size="14px" />
                          </IconButton>
                        ) : (
                          <Button
                            size="small"
                            startIcon={<Crosshair size="14px" />}
                            disabled={threshold === undefined || !viewValidItems.length}
                            onClick={handleJumpToThreshold}
                            sx={{ fontSize: "12px", minWidth: 0, whiteSpace: "nowrap" }}
                          >
                            {threshold !== undefined ? `TH ${threshold}` : "TH -"}
                          </Button>
                        )}
                      </span>
                    </Tooltip>
                    <Tooltip title={t("CompareBasket", "비교 이미지 담기")}>
                      <IconButton size="small" onClick={(e) => setBasketAnchor(e.currentTarget)}>
                        <Badge badgeContent={basket.items.length} color="primary" max={99}>
                          <CollectionsIcon sx={{ fontSize: 16 }} />
                        </Badge>
                      </IconButton>
                    </Tooltip>
                    <Tooltip title={t("TableToolsTooltip")}>
                      <span>
                        <IconButton
                          size="small"
                          disabled={isValidating || !validItems.length}
                          onClick={(e) => setToolsAnchor(e.currentTarget)}
                          color={mismatchFilter ? "primary" : "default"}
                        >
                          <ListFilter size="16px" />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </Box>
                </Box>
                <CompareBasketPopover
                  projectId={String(id)}
                  anchorEl={basketAnchor}
                  onClose={() => setBasketAnchor(null)}
                />
                <Menu
                  open={!!rowMenu}
                  onClose={() => setRowMenu(null)}
                  anchorReference="anchorPosition"
                  anchorPosition={rowMenu ? { top: rowMenu.y, left: rowMenu.x } : undefined}
                >
                  {rowMenu?.from === "train" ? (
                    <MenuItem
                      dense
                      disabled={trainingState[Number(id)] || validatingState[Number(id)]}
                      onClick={() => handleExcludeFromTrain(rowMenu.row)}
                    >
                      {t("ExcludeFromTrain", "학습에서 제외하기")}
                    </MenuItem>
                  ) : (
                    rowMenu &&
                    (basket.has(rowMenu.row.path) ? (
                      <MenuItem
                        dense
                        onClick={() => {
                          basket.remove(rowMenu.row.path);
                          setRowMenu(null);
                        }}
                      >
                        {t("RemoveFromCompare", "비교 이미지에서 빼기")}
                      </MenuItem>
                    ) : (
                      <MenuItem
                        dense
                        onClick={() => {
                          const { path, label, recentLabel, score } = rowMenu.row;
                          const shownLabel = recentLabel ?? (Array.isArray(label) ? label.join(",") : label);
                          basket.add({ path, label: shownLabel, score });
                          setRowMenu(null);
                        }}
                      >
                        {t("AddToCompare", "비교 이미지로 담기")}
                      </MenuItem>
                    ))
                  )}
                </Menu>
                <TableToolsPanel
                  anchorEl={toolsAnchor}
                  onClose={() => setToolsAnchor(null)}
                  mismatchPairs={mismatchPairs}
                  filter={mismatchFilter}
                  onFilterChange={setMismatchFilter}
                  visibleCount={viewValidItems.length}
                  onExportCsv={() =>
                    exportCsv(viewValidItems, `insp_${id}_${new Date().toISOString().slice(0, 10)}.csv`)
                  }
                />

                {validatingState[Number(id)] && (
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: 1.5,
                      flexShrink: 0,
                      pr: 1,
                    }}
                  >
                    <Typography sx={{ fontSize: "14px", fontWeight: 600, color: "var(--dk-text, #333)" }}>
                      {t("Validating")}
                    </Typography>
                    <Box
                      sx={{
                        display: "flex",
                        flexDirection: "column",
                        width: "120px",
                      }}
                    >
                      <Typography
                        sx={{
                          fontSize: "10px",
                          color: "var(--dk-text-2, #666)",
                          textAlign: "right",
                          lineHeight: 1,
                        }}
                      >
                        {Number.isFinite((progress.count / progress.totalCount) * 100)
                          ? Math.floor((progress.count / progress.totalCount) * 100)
                          : 0}
                        %
                      </Typography>

                      <LinearProgress
                        variant="determinate"
                        value={(progress.count / progress.totalCount) * 100}
                        sx={{
                          height: 6,
                          mt: 0.3,
                          borderRadius: 3,
                          backgroundColor: "var(--dk-surface-3, #d8d8d8)",
                          "& .MuiLinearProgress-bar": { borderRadius: 3 },
                        }}
                      />
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: "var(--dk-surface, #fff)",
                        borderRadius: "50%",
                        width: "26px",
                        height: "26px",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                        cursor: "pointer",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                        "&:hover": { backgroundColor: "var(--dk-surface-2, #f5f5f5)" },
                        ml: 1,
                      }}
                    >
                      <CircularProgress size="14px" />
                    </Box>
                  </Box>
                  // ) : (
                  //   <Box sx={{ display: "flex", alignItems: "center" }}>
                  //     <Button
                  //       sx={{ color: "var(--main-text-color)" }}
                  //       onClick={handleValid}
                  //     >
                  //       <PlayArrowIcon />
                  //     </Button>
                  //   </Box>
                )}
              </Box>
              <TableContainer sx={{ flex: 1, overflowY: "auto" }}>
                <Table
                  size="small"
                  sx={{
                    tableLayout: "fixed",
                  }}
                  stickyHeader
                >
                  <TableHead
                    sx={{
                      bgcolor: "var(--dg-table-header-bg-color)",
                    }}
                  >
                    <TableRow>
                      {validFields.map((field, index) => (
                        <TableCell
                          key={field}
                          align="center"
                          sortDirection={sort?.field === field ? sort.dir : false}
                          sx={{
                            color: "var(--dg-table-header-color)",
                            height: "58px",
                            padding: "0 4px",
                            width: headerWidth(field, validFields),
                          }}
                        >
                          {field === "imageName" || field === "score" ? (
                            <TableSortLabel
                              active={sort?.field === field}
                              direction={sort?.field === field ? sort.dir : "asc"}
                              onClick={() =>
                                setSort((prev) =>
                                  prev?.field === field && prev.dir === "asc"
                                    ? { field, dir: "desc" }
                                    : { field, dir: "asc" },
                                )
                              }
                              sx={{
                                color: "inherit",
                                "&.Mui-active, &:hover": { color: "inherit" },
                                "& .MuiTableSortLabel-icon": { color: "inherit !important" },
                              }}
                            >
                              {field}
                            </TableSortLabel>
                          ) : (
                            field
                          )}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                </Table>
                <VirtualizedSelectableList
                  items={viewValidItems}
                  itemHeight={40}
                  heightOffset={58} // 헤더 셀 높이(58px)와 맞춰야 바깥 스크롤이 안 생김
                  selectedItem={selectedRow}
                  listRef={validListRef}
                  onRowClick={handleRowClick}
                  onRowContextMenu={(row: IImageInfo, e: React.MouseEvent) => {
                    e.preventDefault();
                    setRowMenu({ row, x: e.clientX, y: e.clientY, from: "valid" });
                  }}
                  renderRow={(row: IImageInfo, index: number) =>
                    validFields
                      // .filter((field) => !(field === "general" && !more3Channel))
                      .map((field, colIndex) => (
                        <div
                          key={colIndex}
                          style={{
                            flex: `${getColumnWeight(field)} 1 0`,
                            minWidth: 0,
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            textAlign: "center",
                            padding: "0 4px",
                          }}
                        >
                          {renderCellContent(field, row, index)}
                        </div>
                      ))
                  }
                />
              </TableContainer>
            </Box>
          </Panel>
        </PanelGroup>
      </Grid>
      {openHelpImageList && (
        <InspectionDataFlowDiagram open={openHelpImageList} onClose={() => setOpenHelpImageList(false)} />
      )}
      {snapshotTarget && (
        <SnapshotModal
          open
          onClose={() => setSnapshotTarget(null)}
          projectId={Number(id)}
          serverAddress={myAIModule?.serverAddress}
        />
      )}
    </>
  );
}

// 최근 학습 결과 요약: 완료/실패/중단 + 저장된 모델 (없으면 "저장된 모델 없음")
function LastTrainChip({
  lastTrain,
  onClick,
}: {
  lastTrain: {
    trainHistory: { status: string; finish_at: string | null; create_at: string } | null;
    savedModels: { model_uuid: string; name: string }[];
  } | null;
  onClick: () => void;
}) {
  const history = lastTrain?.trainHistory;
  if (!history || history.status === "Training" || history.status === "Pending") return null;

  const time = new Date(history.finish_at ?? history.create_at).toLocaleString();
  const models = lastTrain.savedModels;
  const { text, color, bg } =
    history.status === "Finished"
      ? models.length
        ? { text: t("LastTrainSavedModels", { count: models.length }), color: "#16a34a", bg: "#dcfce7" }
        : { text: t("LastTrainNoSavedModel"), color: "#b45309", bg: "#fef3c7" }
      : history.status === "Failed"
        ? { text: t("LastTrainFailed"), color: "#dc2626", bg: "#fee2e2" }
        : { text: t("LastTrainStopped"), color: "var(--dk-text-2, #6e6e73)", bg: "var(--dk-surface-3, #f5f5f7)" };

  return (
    <Tooltip
      title={
        <Box>
          <Typography sx={{ fontSize: "12px" }}>
            {t("LastTrain")}: {time}
          </Typography>
          {models.map((m) => (
            <Typography key={m.model_uuid} sx={{ fontSize: "12px" }}>
              · {m.name}
            </Typography>
          ))}
        </Box>
      }
    >
      <Box
        onClick={onClick}
        sx={{
          ml: 1,
          px: "8px",
          height: 22,
          borderRadius: "6px",
          fontSize: "12px",
          fontWeight: 600,
          color,
          background: bg,
          // 파스텔 배경은 다크에서 글자색을 옅게 깐 배경으로
          ".dark &": { background: `color-mix(in srgb, ${color} 20%, transparent)` },
          cursor: "pointer",
          whiteSpace: "nowrap",
          minWidth: 0,
          overflow: "hidden",
          textOverflow: "ellipsis",
          display: "block",
          lineHeight: "22px",
        }}
      >
        {text}
      </Box>
    </Tooltip>
  );
}
